import React, { useEffect, useRef, useState } from 'react';
import QuillEditor from 'react-quill';
import { Loading } from '../../../../components/Loading';
import { useGetReportTextTemplate } from '../../../../traits/api/reportTextTemplates/useGetReportTextTemplate';
import { useUpdateReportTextTemplate } from '../../../../traits/api/reportTextTemplates/useUpdateReportTextTemplate';
import {
  EMPTY_QUILL_VALUE,
  isEmptyQuillValue,
  normalizeForQuill,
} from '../../../controls/utils/normalizeForQuill';
import {
  PERSONDATA_TOPICS,
  PersondataTopicConfig,
  ReportTextTemplateKey,
  TemplateVariant,
} from '../../reportTextTemplateKeys';
import { S } from './ReportTemplates.styles';

enum Tab {
  AssignmentDescription = 'assignmentDescription',
  SummaryAndRecommendations = 'summaryAndRecommendations',
  Persondata = 'persondata',
}

const ASSIGNMENT_PLACEHOLDERS = [
  '{{date}}',
  '{{completedAt}}',
  '{{projectName}}',
  '{{address}}',
  '{{city}}',
  '{{clientName}}',
  '{{mainContractor}}',
];

const formats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'color',
];

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'link'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

export const ReportTemplates: React.FC = () => {
  const [tab, setTab] = useState<Tab>(Tab.AssignmentDescription);

  return (
    <S.Page>
      <S.Header>
        <S.H2 $weight={600}>Mallar</S.H2>
        <S.B3 $color="darkGray">
          Redigera standardtexter som används när du genererar
          uppdragsbeskrivning, sammanfattning och Resultat/åtgärder från
          Persondata i en kontroll.
        </S.B3>
        <S.NavigationContainer>
          <S.NavLink
            $isActive={tab === Tab.AssignmentDescription}
            onClick={() => setTab(Tab.AssignmentDescription)}
          >
            Uppdragsbeskrivning
          </S.NavLink>
          <S.NavLink
            $isActive={tab === Tab.SummaryAndRecommendations}
            onClick={() => setTab(Tab.SummaryAndRecommendations)}
          >
            Sammanfattning
          </S.NavLink>
          <S.NavLink
            $isActive={tab === Tab.Persondata}
            onClick={() => setTab(Tab.Persondata)}
          >
            Resultat/åtgärder
          </S.NavLink>
        </S.NavigationContainer>
      </S.Header>

      {tab === Tab.Persondata ? (
        <PersondataTemplates />
      ) : (
        <TemplateEditor key={tab} templateKey={tab} />
      )}
    </S.Page>
  );
};

const TemplateEditor: React.FC<{
  templateKey: ReportTextTemplateKey;
}> = ({ templateKey }) => {
  const template = useGetReportTextTemplate(templateKey);
  const updateTemplate = useUpdateReportTextTemplate(templateKey);
  const [body, setBody] = useState('');
  const hasInitialized = useRef(false);

  useEffect(() => {
    hasInitialized.current = false;
  }, [templateKey]);

  useEffect(() => {
    if (template.data && !hasInitialized.current) {
      setBody(
        templateKey === 'summaryAndRecommendations'
          ? normalizeForQuill(template.data.body)
          : template.data.body
      );
      hasInitialized.current = true;
    }
  }, [template.data, templateKey]);

  if (template.isLoading || !template.data) {
    return (
      <S.Body>
        <Loading />
      </S.Body>
    );
  }

  const handleSave = async () => {
    await updateTemplate.mutateAsync({ body });
  };

  const isAssignment = templateKey === 'assignmentDescription';

  return (
    <S.Body>
      {isAssignment ? (
        <React.Fragment>
          <S.H6>Platshållare</S.H6>
          <S.PlaceholderList>
            {ASSIGNMENT_PLACEHOLDERS.map((placeholder) => (
              <li key={placeholder}>{placeholder}</li>
            ))}
          </S.PlaceholderList>
          <S.Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Skriv malltext här..."
          />
        </React.Fragment>
      ) : (
        <S.EditorContainer>
          <QuillEditor
            formats={formats}
            modules={modules}
            theme="snow"
            value={body || EMPTY_QUILL_VALUE}
            onChange={(content, _delta, source) => {
              if (source !== 'user') return;
              setBody(isEmptyQuillValue(content) ? '' : content);
            }}
          />
        </S.EditorContainer>
      )}

      <S.Footer>
        <S.Button
          onClick={handleSave}
          disabled={updateTemplate.isLoading || body.trim().length === 0}
        >
          {updateTemplate.isLoading ? 'Sparar...' : 'Spara'}
        </S.Button>
      </S.Footer>
    </S.Body>
  );
};

const PersondataTemplates: React.FC = () => {
  return (
    <S.Body>
      <S.B3 $color="darkGray">
        Utkastmallar för Persondata-driven generering. Välj variant utan
        avvikelser eller med avvikelser för Resultat respektive Åtgärder.
        Platshållare använder{' '}
        <code>{'{{fältnamn}}'}</code> enligt kontrollstatistik.
      </S.B3>

      <S.TopicAccordion type="multiple" defaultValue={['workRegister']}>
        {PERSONDATA_TOPICS.map((topic) => (
          <TopicEditors key={topic.id} topic={topic} />
        ))}
      </S.TopicAccordion>
    </S.Body>
  );
};

const TopicEditors: React.FC<{ topic: PersondataTopicConfig }> = ({
  topic,
}) => {
  return (
    <S.TopicItem value={topic.id}>
      <S.TopicTrigger>
        <S.H6>{topic.title}</S.H6>
        <S.TopicChevron src="ChevronDown" />
      </S.TopicTrigger>
      <S.TopicContent>
        <VariantEditor
          label="Resultat"
          placeholders={topic.placeholders}
          keys={topic.result}
        />
        <VariantEditor
          label="Åtgärder"
          placeholders={topic.placeholders}
          keys={topic.actions}
        />
      </S.TopicContent>
    </S.TopicItem>
  );
};

const VariantEditor: React.FC<{
  label: string;
  placeholders: string[];
  keys: Record<TemplateVariant, ReportTextTemplateKey>;
}> = ({ label, placeholders, keys }) => {
  const [variant, setVariant] = useState<TemplateVariant>('WhenClear');
  const templateKey = keys[variant];

  return (
    <S.FieldBlock>
      <S.FieldHeader>
        <S.H6>{label}</S.H6>
        <S.VariantTabs>
          <S.VariantTab
            $isActive={variant === 'WhenClear'}
            onClick={() => setVariant('WhenClear')}
            type="button"
          >
            Utan avvikelser
          </S.VariantTab>
          <S.VariantTab
            $isActive={variant === 'WhenDeviations'}
            onClick={() => setVariant('WhenDeviations')}
            type="button"
          >
            Med avvikelser
          </S.VariantTab>
        </S.VariantTabs>
      </S.FieldHeader>

      <S.H6>Platshållare</S.H6>
      <S.PlaceholderList>
        {placeholders.map((placeholder) => (
          <li key={placeholder}>{placeholder}</li>
        ))}
      </S.PlaceholderList>

      <QuillTemplateEditor key={templateKey} templateKey={templateKey} />
    </S.FieldBlock>
  );
};

const QuillTemplateEditor: React.FC<{
  templateKey: ReportTextTemplateKey;
}> = ({ templateKey }) => {
  const template = useGetReportTextTemplate(templateKey);
  const updateTemplate = useUpdateReportTextTemplate(templateKey);
  const [body, setBody] = useState('');
  const hasInitialized = useRef(false);

  useEffect(() => {
    hasInitialized.current = false;
  }, [templateKey]);

  useEffect(() => {
    if (template.data && !hasInitialized.current) {
      setBody(normalizeForQuill(template.data.body));
      hasInitialized.current = true;
    }
  }, [template.data, templateKey]);

  if (template.isLoading || !template.data) {
    return <Loading />;
  }

  const handleSave = async () => {
    await updateTemplate.mutateAsync({ body });
  };

  return (
    <React.Fragment>
      <S.EditorContainer>
        <QuillEditor
          formats={formats}
          modules={modules}
          theme="snow"
          value={body || EMPTY_QUILL_VALUE}
          onChange={(content, _delta, source) => {
            if (source !== 'user') return;
            setBody(isEmptyQuillValue(content) ? '' : content);
          }}
        />
      </S.EditorContainer>
      <S.Footer>
        <S.Button
          onClick={handleSave}
          disabled={updateTemplate.isLoading || body.trim().length === 0}
        >
          {updateTemplate.isLoading ? 'Sparar...' : 'Spara'}
        </S.Button>
      </S.Footer>
    </React.Fragment>
  );
};
